import { ColDef } from "ag-grid-community";
import { useState } from "react";
import { McTitle, SegmentedToggle } from "../../../components";
import McDropdownNew from "../../../components/McDropdown/MCDropdownNew";
import ViewEditTable from "../../../components/ViewEditTable/ViewEditTable";
import { useAppDispatch } from "../../../hooks";
import { doGetFeatureToggle, doUpdateFeatureToggle } from "../admin/restQueries";
import { BATCHJOBTEMPLATES } from "../batchjob/batchJobConstants";
import { setShowSuccessMessageThunk } from "../../../global/globalSlice";
import { IFeatureToggle, IValueLabelGeneric } from "@mctypes/tempTypes";
import { Stack, Flex } from "@mantine/core";

type TProjectId = { projectId: string };

export const ActiveBatchJob = () => {
  const dispatch = useAppDispatch();
  const [currentFeatureToggle, setCurrentFeatureToggle] = useState<IFeatureToggle>(null);

  const TEMPLATES: IValueLabelGeneric<string>[] = BATCHJOBTEMPLATES.map((jobName) => {
    return {
      value: jobName,
      label: jobName,
    };
  });

  const save = async (feature: IFeatureToggle) => {
    try {
      const updated = await doUpdateFeatureToggle(feature);
      setCurrentFeatureToggle(updated);
      dispatch(setShowSuccessMessageThunk("Feature updated!"));
    } catch (e) {
      dispatch(setShowSuccessMessageThunk("Error updating feature!"));
    }
  };

  const columns: ColDef<TProjectId>[] = [
    {
      field: "projectId",
      headerName: "Id",
      editable: true,
      flex: 1,
    },
  ];

  const updateFeatureStatus = async (isActive: boolean) => {
    if (currentFeatureToggle === null) {
      return;
    }
    currentFeatureToggle.active = isActive;
    await save(currentFeatureToggle);
  };

  const getFeatureToggle = async (featureName: string) => {
    if (featureName === currentFeatureToggle?.featureName) {
      setCurrentFeatureToggle(null);
      return;
    }
    const _featureToggle = await doGetFeatureToggle("batchJob", featureName);
    setCurrentFeatureToggle(_featureToggle);
  };

  const updateProjects = async (projects: TProjectId[]) => {
    const updatedFeatureToggle: IFeatureToggle = {
      ...currentFeatureToggle,
      projects: projects
        .map((item) => item.projectId)
        .reduce((acc: string[], curr: string) => {
          // remove duplicates and empty strings
          if (curr != null && curr !== "" && acc.includes(curr) === false) {
            return acc.concat([curr]);
          } else {
            return acc;
          }
        }, [])
        // make sure strings are mongodb object ids
        .filter((id: string) => id.length === 24),
    };

    await save(updatedFeatureToggle);
  };

  return (
    <Stack gap={0}>
      <Flex style={{ maxWidth: "50vw" }}>
        <McTitle title="Template" style={{ width: "300px", marginRight: "0.5rem" }}>
          <McDropdownNew
            currentValue={TEMPLATES.find((b) => b.value === currentFeatureToggle?.featureName)}
            options={TEMPLATES}
            displayKey="label"
            onChange={(_, newValue) => getFeatureToggle(newValue.value)}
          />
        </McTitle>
        <McTitle title="Active" style={{ marginRight: "0.5rem" }}>
          <Flex style={{ alignItems: "center", height: "100%" }}>
            <CheckMark
              icon="check"
              status={currentFeatureToggle?.active}
              activeColor="#00975f"
              disabled={currentFeatureToggle === null}
              handleOnChange={(active) => updateFeatureStatus(active)}
            />
          </Flex>
        </McTitle>
        <McTitle title="Access" style={{ marginRight: "0.5rem" }}>
          <SegmentedToggle
            value={currentFeatureToggle?.access ?? "PROJECT"}
            disabled={currentFeatureToggle === null || currentFeatureToggle?.active === false}
            onChange={(access) => {
              if (currentFeatureToggle == null) return;
              const updatedFeatureToggle: IFeatureToggle = {
                ...currentFeatureToggle,
                access: access as IFeatureToggle["access"],
              };
              save(updatedFeatureToggle);
            }}
            options={[
              { value: "PROJECT", label: "PROJECT" },
              { value: "GLOBAL", label: "GLOBAL" },
            ]}
          />
        </McTitle>
      </Flex>
      {currentFeatureToggle?.access === "PROJECT" && currentFeatureToggle.active === true && (
        <McTitle title="Projects" style={{ marginTop: "0.5rem" }}>
          <ViewEditTable
            columns={columns}
            data={currentFeatureToggle?.projects.map((p) => ({ projectId: p })) ?? []}
            onSave={(items) => updateProjects(items)}
            newItemTemplateObject={{ projectId: "" }}
            canEdit={true}
          />
        </McTitle>
      )}
    </Stack>
  );
};

const CheckMark = ({
  icon,
  status,
  activeColor,
  disabled,
  handleOnChange,
}: {
  status: boolean;
  icon: string;
  disabled: boolean;
  activeColor: string;
  handleOnChange: (status: boolean) => void;
}) => {
  return (
    <Flex
      onClick={() => disabled === false && handleOnChange(!status)}
      style={{
        color: "#fff",
        width: "25px",
        height: "25px",
        borderRadius: "50%",
        cursor: disabled === false ? "pointer" : "default",
        backgroundColor: disabled === false && status === true ? activeColor : "#ccc",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <i className={`fa fa-${icon}`} />
    </Flex>
  );
};
